import { sessions } from '~api';
import { HealthIntegration } from '~services';
import { mapGetters } from 'vuex';

const MAXIMUM_DURATION = 7200;
const SAVE_DATA_INTERVAL = 10000;
const WATCHED_THRESHOLD = 30;
const viewingTimeMixin = {
    data() {
        return {
            updateWatchTimeInterval: null,
            interval: null,
            sessionViewId: null,
            totalSeconds: 0,
            tempTotalSeconds: 0,
            health: new HealthIntegration()
        };
    },
    beforeDestroy() {
        // Vue 2 doesn't await async beforeDestroy, so flushFinal runs
        // detached — that's fine: the API retry and Health write are
        // self-contained and don't touch component state.
        this.flushFinal();
        clearInterval(this.updateWatchTimeInterval);
        this.stopTimer();
        sessions.checkGoals();
    },
    computed: {
        ...mapGetters('auth', ['isMember']),
        sessionType() {
            return this.session.type === 'recorded' ? 'recorded' : 'live';
        }
    },
    watch: {
        totalSeconds(val) {
            if (val === 30) {
                sessions.setWatched(this.session.id);
            }
        }
    },
    methods: {
        initUpdateViewingTime() {
            this.startTimer();
            this.updateWatchTimeInterval = setInterval(
                () => this.updateViewSession(),
                SAVE_DATA_INTERVAL
            );
        },
        /**
         * Start timer
         */
        startTimer() {
            if (this.interval) return;
            this.interval = setInterval(() => {
                this.totalSeconds += 1;
            }, 1000);
        },

        /**
         * Stop timer
         */
        stopTimer() {
            clearInterval(this.interval);
            delete this.interval;
        },

        /**
         * Save viewed time
         */
        async saveViewSession() {
            if (this.sessionViewId) return;

            // View data for meditation
            let viewData = {
                time: '0',
                sessionId: this.session.id,
                type: this.sessionType
            };

            // Send view data for statistics when set after mood
            if (this.isMember) {
                const resp = await sessions.saveWatchedTime(viewData);

                if (resp.ok) this.sessionViewId = resp.data.id;
            }
        },

        /**
         * Update viewed time. Intermediate ticks fire-and-forget — each
         * carries an absolute cumulative time, so a dropped one self-heals
         * on the next tick. The final flush passes `retry: true` so the
         * last write doesn't silently drop on a transient blip.
         */
        updateViewSession({ retry = false } = {}) {
            if (
                !this.sessionViewId ||
                this.tempTotalSeconds === this.totalSeconds ||
                this.totalSeconds > MAXIMUM_DURATION
            )
                return;
            this.tempTotalSeconds = this.totalSeconds;

            let viewData = {
                id: this.sessionViewId,
                time: this.totalSeconds.toString(),
                sessionId: this.session.id,
                type: this.sessionType
            };

            if (!this.isMember) return;

            return retry
                ? sessions.updateWatchedTimeWithRetry(viewData)
                : sessions.updateWatchedTime(viewData);
        },

        async flushFinal() {
            try {
                await this.updateViewSession({ retry: true });
            } catch (err) {
                console.warn('[viewingTime] final flush failed', err);
            }
            await this.writeMindfulSessionToHealth();
        },

        // Mirror the watched session into HealthKit / Health Connect.
        // Skipped under the watched threshold so quickly-aborted views
        // don't pollute the user's mindful-minutes history.
        async writeMindfulSessionToHealth() {
            if (this.totalSeconds < WATCHED_THRESHOLD) return;
            if (!this.session?.id) return;
            const endDate = new Date();
            const startDate = new Date(
                endDate.getTime() - this.totalSeconds * 1000
            );
            const sessionViewId =
                this.sessionViewId || `${this.session.id}-${Date.now()}`;
            try {
                await this.health.storeMindfulSession({
                    sessionId: `session-view-${sessionViewId}`,
                    startDate,
                    endDate,
                    durationSeconds: this.totalSeconds,
                    metadata: {
                        source: 'session',
                        sessionId: String(this.session.id),
                        sessionType: this.sessionType
                    }
                });
            } catch (err) {
                console.warn('[viewingTime] health write failed', err);
            }
        }
    }
};

export default viewingTimeMixin;
